import { DashboardAssetType, DashboardSectionId } from '../types.js';

type DashboardVisualScenarioMode = 'desktop' | 'mobile';

type DashboardVisualScenarioModal =
  | { kind: 'asset'; assetId: string }
  | { kind: 'project'; projectId: string }
  | { kind: 'attachment'; threadId: string }
  | { kind: 'security' };

export interface DashboardVisualScenario {
  id: string;
  label: string;
  mode: DashboardVisualScenarioMode;
  viewport: {
    width: number;
    height: number;
  };
  section: DashboardSectionId;
  assetTab?: DashboardAssetType;
  threadId?: string;
  modal?: DashboardVisualScenarioModal;
  referenceImage: string | null;
  notes: string[];
  maxDiffPixelRatio?: number;
}

const STITCH_ROOT = 'stitch_futuristic_3d_viewer_variant_2';

export const DASHBOARD_VISUAL_SCENARIOS: DashboardVisualScenario[] = [
  {
    id: 'projects-desktop',
    label: 'Projects desktop dashboard',
    mode: 'desktop',
    viewport: { width: 1600, height: 1280 },
    section: 'projects',
    referenceImage: `${STITCH_ROOT}/mvp_dashboard_projects_page/screen.png`,
    notes: [
      'Maps the projects export to the shared single-page dashboard scrolled to the projects section.',
      'The intro shell remains mounted above the section but is pushed out of frame by the harness scroll position.',
    ],
  },
  {
    id: 'assets-desktop',
    label: 'Assets desktop dashboard',
    mode: 'desktop',
    viewport: { width: 1600, height: 1280 },
    section: 'assets',
    assetTab: 'model',
    referenceImage: `${STITCH_ROOT}/mvp_dashboard_assets_view_full_height_sidebars/screen.png`,
    notes: ['Maps the assets export to the model tab in the shared dashboard shell.'],
  },
  {
    id: 'messages-desktop',
    label: 'Messages desktop dashboard',
    mode: 'desktop',
    viewport: { width: 1600, height: 1280 },
    section: 'messages',
    threadId: 'acme',
    referenceImage: `${STITCH_ROOT}/mvp_dashboard_messages_view_updated_navigation/screen.png`,
    notes: ['Maps the messages export to the first seeded conversation thread.'],
  },
  {
    id: 'settings-desktop',
    label: 'Settings desktop dashboard',
    mode: 'desktop',
    viewport: { width: 1600, height: 1304 },
    section: 'settings',
    referenceImage: `${STITCH_ROOT}/minimalist_dashboard_settings/screen.png`,
    notes: ['Matches the exported settings screen with the account settings section active.'],
  },
  {
    id: 'asset-model-modal-desktop',
    label: '3D asset modal desktop',
    mode: 'desktop',
    viewport: { width: 1600, height: 1280 },
    section: 'assets',
    assetTab: 'model',
    modal: { kind: 'asset', assetId: 'engine-main-v2' },
    referenceImage: `${STITCH_ROOT}/refactored_3d_asset_detail_modal/screen.png`,
    notes: ['Uses the seeded 3D model asset to mirror the exported desktop model detail modal.'],
  },
  {
    id: 'asset-image-modal-desktop',
    label: 'Image asset modal desktop',
    mode: 'desktop',
    viewport: { width: 1600, height: 1280 },
    section: 'assets',
    assetTab: 'image',
    modal: { kind: 'asset', assetId: 'hero-shot' },
    referenceImage: `${STITCH_ROOT}/standardized_image_asset_modal_cinematic_bg/screen.png`,
    notes: ['Uses the seeded image asset to mirror the exported desktop image detail modal.'],
  },
  {
    id: 'asset-pdf-modal-desktop',
    label: 'PDF asset modal desktop',
    mode: 'desktop',
    viewport: { width: 1600, height: 1280 },
    section: 'assets',
    assetTab: 'pdf',
    modal: { kind: 'asset', assetId: 'assembly-guide' },
    referenceImage: `${STITCH_ROOT}/standardized_pdf_asset_modal_cinematic_bg/screen.png`,
    notes: ['Uses the seeded PDF asset to mirror the exported desktop PDF detail modal.'],
  },
  {
    id: 'asset-video-modal-desktop',
    label: 'Video asset modal desktop',
    mode: 'desktop',
    viewport: { width: 1600, height: 1280 },
    section: 'assets',
    assetTab: 'video',
    modal: { kind: 'asset', assetId: 'tutorial-video' },
    referenceImage: `${STITCH_ROOT}/standardized_video_asset_modal_cinematic_bg/screen.png`,
    notes: ['Uses the seeded video asset to mirror the exported desktop video detail modal.'],
  },
  {
    id: 'projects-mobile',
    label: 'Projects mobile dashboard',
    mode: 'mobile',
    viewport: { width: 686, height: 1600 },
    section: 'projects',
    referenceImage: `${STITCH_ROOT}/mobile_projects_dashboard/screen.png`,
    notes: ['Matches the mobile projects export at its native width of 686px.'],
  },
  {
    id: 'assets-mobile',
    label: 'Assets mobile dashboard',
    mode: 'mobile',
    viewport: { width: 633, height: 1600 },
    section: 'assets',
    assetTab: 'model',
    referenceImage: `${STITCH_ROOT}/mobile_assets_dashboard/screen.png`,
    notes: ['Matches the mobile assets export at its native width of 633px.'],
  },
  {
    id: 'messages-mobile',
    label: 'Messages mobile dashboard',
    mode: 'mobile',
    viewport: { width: 647, height: 1600 },
    section: 'messages',
    threadId: 'acme',
    referenceImage: `${STITCH_ROOT}/updated_mobile_team_chat_view/screen.png`,
    notes: ['Maps the updated mobile team chat export to the first seeded thread conversation.'],
  },
  {
    id: 'settings-mobile',
    label: 'Settings mobile dashboard',
    mode: 'mobile',
    viewport: { width: 706, height: 1600 },
    section: 'settings',
    referenceImage: `${STITCH_ROOT}/refined_mobile_setup_view/screen.png`,
    notes: ['Matches the mobile settings export at its native width of 706px.'],
  },
  {
    id: 'asset-model-modal-mobile',
    label: '3D asset modal mobile',
    mode: 'mobile',
    viewport: { width: 706, height: 1600 },
    section: 'assets',
    assetTab: 'model',
    modal: { kind: 'asset', assetId: 'engine-main-v2' },
    referenceImage: `${STITCH_ROOT}/mobile_3d_asset_detail_modal/screen.png`,
    notes: ['Uses the mobile 3D asset export width of 706px and the seeded model asset modal state.'],
  },
  {
    id: 'asset-image-modal-mobile',
    label: 'Image asset modal mobile',
    mode: 'mobile',
    viewport: { width: 706, height: 1600 },
    section: 'assets',
    assetTab: 'image',
    modal: { kind: 'asset', assetId: 'hero-shot' },
    referenceImage: `${STITCH_ROOT}/mobile_image_asset_modal/screen.png`,
    notes: ['Uses the mobile image export width of 706px and the seeded image asset modal state.'],
  },
  {
    id: 'asset-pdf-modal-mobile',
    label: 'PDF asset modal mobile',
    mode: 'mobile',
    viewport: { width: 706, height: 1600 },
    section: 'assets',
    assetTab: 'pdf',
    modal: { kind: 'asset', assetId: 'assembly-guide' },
    referenceImage: `${STITCH_ROOT}/mobile_pdf_asset_modal/screen.png`,
    notes: ['Uses the mobile PDF export width of 706px and the seeded PDF asset modal state.'],
  },
  {
    id: 'asset-video-modal-mobile',
    label: 'Video asset modal mobile',
    mode: 'mobile',
    viewport: { width: 706, height: 1600 },
    section: 'assets',
    assetTab: 'video',
    modal: { kind: 'asset', assetId: 'tutorial-video' },
    referenceImage: `${STITCH_ROOT}/mobile_video_asset_modal/screen.png`,
    notes: ['Uses the mobile video export width of 706px and the seeded video asset modal state.'],
  },
  {
    id: 'project-action-modal-desktop',
    label: 'Project action modal desktop',
    mode: 'desktop',
    viewport: { width: 1600, height: 1280 },
    section: 'projects',
    modal: { kind: 'project', projectId: 'v8-turbo' },
    referenceImage: null,
    notes: ['Captured for coverage because the app exposes this modal, but no Stitch PNG export exists for image comparison.'],
  },
  {
    id: 'attachment-modal-desktop',
    label: 'Attachment modal desktop',
    mode: 'desktop',
    viewport: { width: 1600, height: 1280 },
    section: 'messages',
    threadId: 'acme',
    modal: { kind: 'attachment', threadId: 'acme' },
    referenceImage: null,
    notes: ['Captured for coverage because the app exposes this modal, but no Stitch PNG export exists for image comparison.'],
  },
  {
    id: 'security-modal-desktop',
    label: 'Security modal desktop',
    mode: 'desktop',
    viewport: { width: 1600, height: 1280 },
    section: 'settings',
    modal: { kind: 'security' },
    referenceImage: null,
    notes: ['Captured for coverage because the app exposes this modal, but no Stitch PNG export exists for image comparison.'],
  },
  {
    id: 'project-action-modal-mobile',
    label: 'Project action modal mobile',
    mode: 'mobile',
    viewport: { width: 686, height: 1600 },
    section: 'projects',
    modal: { kind: 'project', projectId: 'v8-turbo' },
    referenceImage: null,
    notes: ['Captured for coverage at the mobile projects viewport, but no Stitch PNG export exists for image comparison.'],
  },
  {
    id: 'attachment-modal-mobile',
    label: 'Attachment modal mobile',
    mode: 'mobile',
    viewport: { width: 647, height: 1600 },
    section: 'messages',
    threadId: 'acme',
    modal: { kind: 'attachment', threadId: 'acme' },
    referenceImage: null,
    notes: ['Captured for coverage at the mobile messages viewport, but no Stitch PNG export exists for image comparison.'],
  },
  {
    id: 'security-modal-mobile',
    label: 'Security modal mobile',
    mode: 'mobile',
    viewport: { width: 706, height: 1600 },
    section: 'settings',
    modal: { kind: 'security' },
    referenceImage: null,
    notes: ['Captured for coverage at the mobile settings viewport, but no Stitch PNG export exists for image comparison.'],
  },
];

export function getDashboardVisualScenario(scenarioId: string | null | undefined): DashboardVisualScenario | null {
  if (!scenarioId) {
    return DASHBOARD_VISUAL_SCENARIOS[0] ?? null;
  }

  return DASHBOARD_VISUAL_SCENARIOS.find((scenario) => scenario.id === scenarioId) ?? null;
}
